import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { cn } from "../../lib/utils"
import { useBrowserModePortalContainer } from "@/features/browser-mode"
import { useClearStuckBodyPointerEventsOnUnmount } from "./useClearStuckBodyPointerEvents"
import { useModalScrimPresence } from "@/layout/store/modalScrimStore"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  // The Windows caption buttons are composited above the page, so this scrim
  // cannot reach them; publishing its presence lets the title bar have the
  // main process repaint them dimmed (useCaptionButtonScrim.ts).
  useModalScrimPresence();

  return (
    <DialogPrimitive.Overlay
      ref={ref}
      data-slot="dialog-overlay"
      className={cn(
        // `app-region-no-drag`: this overlay is portalled to <body> and covers the
        // viewport, so its top strip lies over the integrated title bar's drag
        // region (CH-02). Without the opt-out, clicking there to dismiss the
        // dialog drags the window instead.
        //
        // The scrim dims to 45%, not shadcn's stock 80% (CH-22). At 80% the app
        // behind the dialog is effectively blacked out — a web-modal convention.
        // A native panel leaves the window it belongs to perceptible behind it,
        // so you keep your bearings while the dialog is up.
        "app-region-no-drag fixed inset-0 z-(--z-overlay) bg-black/45 [&[data-state=open]]:animate-in [&[data-state=closed]]:animate-out [&[data-state=closed]]:fade-out-0 [&[data-state=open]]:fade-in-0 [&[data-state=closed]]:pointer-events-none",
        className
      )}
      {...props}
    />
  );
})
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => {
  // In browser mode, use the portal container to scope dialogs within the app window
  const portalContainer = useBrowserModePortalContainer();

  // Radix layer-dismiss race guard — see useClearStuckBodyPointerEvents.ts.
  useClearStuckBodyPointerEventsOnUnmount();

  return (
    <DialogPortal container={portalContainer}>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        data-slot="dialog-content"
        className={cn(
          // Entrance drops the zoom (a modal settling into place isn't a
          // menu popping open) but keeps its fade + slide; exit keeps the
          // soft zoom-out (CH-19/CH-21). Do not reintroduce `zoom-in-*`.
          //
          // Elevation (CH-22/PP-31): `bg-surface-raised` is a real step up
          // from the canvas on dark and resolves to the same white as
          // `--background` on light — deliberately, because on a white canvas
          // the platform convention is that elevation comes from border +
          // shadow, not a tinted fill. `.dialog-elevation` (src/index.css)
          // supplies the layered shadow in both themes; it replaces
          // `shadow-lg`, which is invisible on the dark canvas.
          "fixed left-[50%] top-[50%] z-(--z-overlay) grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border border-border bg-surface-raised p-6 text-foreground dialog-elevation duration-200 [&[data-state=open]]:animate-in [&[data-state=closed]]:animate-out [&[data-state=closed]]:fade-out-0 [&[data-state=open]]:fade-in-0 [&[data-state=closed]]:zoom-out-95 [&[data-state=closed]]:slide-out-to-left-1/2 [&[data-state=closed]]:slide-out-to-top-[48%] [&[data-state=open]]:slide-in-from-left-1/2 [&[data-state=open]]:slide-in-from-top-[48%] sm:rounded-lg",
          className
        )}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} 
